<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="1100px">
      <v-skeleton-loader
        v-if="loading"
        type="article, actions"
      ></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !data.id
                  ? $t("wkt.admin.training-action.new-window.title")
                  : $t("wkt.admin.training-action.edit-window.title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text @wheel="onWheel">
          <v-container>
            <v-form ref="form">
              <v-tabs v-model="tab" grow>
                <v-tab>{{ $t("wkt.admin.training-action.tabs.general") }}</v-tab>
                <v-tab>{{ $t("wkt.admin.training-action.tabs.planning") }}</v-tab>
                <v-tab>{{ $t("wkt.admin.training-action.tabs.content") }}</v-tab>
                <v-tab>{{ $t("wkt.admin.training-action.tabs.economy") }}</v-tab>
                <v-tab>{{ $t("wkt.admin.training-action.tabs.enrollments-management") }}</v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab">
                <v-tab-item>
                  <v-container fluid class="px-0 pt-4">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.reference"
                          :label="$t('wkt.admin.training-action.edit-window.reference')"
                          autocomplete="off"
                          maxlength="100"
                          counter="100"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                      <v-col cols="12" md="8">
                        <v-autocomplete
                          v-model="data.courseId"
                          :items="trainingCoursesWithEmptyOption"
                          item-text="courseLabel"
                          item-value="id"
                          :label="$t('wkt.admin.training-action.edit-window.course')"
                          :disabled="isReadOnly"
                          :menu-props="trainingActionDialogMenuProps"
                          clearable
                          @change="onCourseChanged"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model="data.name"
                          :label="$t('wkt.admin.training-action.edit-window.name')"
                          autocomplete="off"
                          :rules="[validationRules.required]"
                          maxlength="150"
                          counter="150"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="data.trainingCategoryId"
                          :items="trainingCategories"
                          item-text="name"
                          item-value="id"
                          :label="$t('wkt.admin.training-action.edit-window.training-category')"
                          :rules="[validationRules.required]"
                          :disabled="isReadOnly"
                          :menu-props="trainingActionDialogMenuProps"
                          clearable
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="data.description"
                          :label="$t('wkt.admin.training-action.edit-window.description')"
                          auto-grow
                          rows="3"
                          maxlength="2000"
                          counter="2000"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tab-item>

                <v-tab-item>
                  <v-container fluid class="px-0 pt-4">
                    <v-row>
                      <v-col cols="12">
                        <div style="font-size: 12px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #757575;">
                          {{ $t("wkt.admin.training-action.edit-window.section.logistics") }}
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="data.mode"
                          :items="modeOptions"
                          item-text="name"
                          item-value="id"
                          :label="$t('wkt.admin.training-action.edit-window.mode')"
                          :disabled="isReadOnly"
                          :menu-props="trainingActionDialogMenuProps"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.durationHours"
                          :label="$t('wkt.admin.training-action.edit-window.duration-hours')"
                          type="number"
                          step="0.25"
                          min="0"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.instructorText"
                          :label="$t('wkt.admin.training-action.edit-window.instructor')"
                          maxlength="250"
                          counter="250"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.locationText"
                          :label="$t('wkt.admin.training-action.edit-window.location')"
                          maxlength="500"
                          counter="500"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model="data.scheduleText"
                          :label="$t('wkt.admin.training-action.edit-window.schedule')"
                          maxlength="500"
                          counter="500"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <div style="font-size: 12px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #757575; margin-top: 8px;">
                          {{ $t("wkt.admin.training-action.edit-window.section.calendar-capacity") }}
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="3">
                        <v-menu ref="enrollmentStartDateMenu" v-model="enrollmentStartDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="enrollmentStartDateFormatted"
                              :label="$t('wkt.admin.training-action.edit-window.enrollment-start')"
                              v-on="on"
                              v-bind="attrs"
                              readonly
                              :disabled="isReadOnly"
                              :error="enrollmentDateError && !enrollmentStartDate"
                              prepend-icon="mdi-calendar"
                            />
                          </template>
                          <v-date-picker v-model="enrollmentStartDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="enrollmentStartDateMenu = false" />
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-menu ref="enrollmentEndDateMenu" v-model="enrollmentEndDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="enrollmentEndDateFormatted"
                              :label="$t('wkt.admin.training-action.edit-window.enrollment-end')"
                              v-on="on"
                              v-bind="attrs"
                              readonly
                              :disabled="isReadOnly"
                              :error="enrollmentDateError && !enrollmentEndDate"
                              prepend-icon="mdi-calendar"
                            />
                          </template>
                          <v-date-picker v-model="enrollmentEndDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="enrollmentEndDateMenu = false" />
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-menu ref="realizationStartDateMenu" v-model="realizationStartDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="realizationStartDateFormatted"
                              :label="$t('wkt.admin.training-action.edit-window.realization-start')"
                              v-on="on"
                              v-bind="attrs"
                              readonly
                              :disabled="isReadOnly"
                              prepend-icon="mdi-calendar"
                            />
                          </template>
                          <v-date-picker v-model="realizationStartDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="realizationStartDateMenu = false" />
                        </v-menu>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-menu ref="realizationEndDateMenu" v-model="realizationEndDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="realizationEndDateFormatted"
                              :label="$t('wkt.admin.training-action.edit-window.realization-end')"
                              v-on="on"
                              v-bind="attrs"
                              readonly
                              :disabled="isReadOnly"
                              prepend-icon="mdi-calendar"
                            />
                          </template>
                          <v-date-picker v-model="realizationEndDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="realizationEndDateMenu = false" />
                        </v-menu>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="data.minSeats"
                          :label="$t('wkt.admin.training-action.edit-window.min-seats')"
                          type="number"
                          min="0"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="data.maxSeats"
                          :label="$t('wkt.admin.training-action.edit-window.max-seats')"
                          type="number"
                          min="0"
                          :disabled="isReadOnly"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-select
                          v-model="data.resultType"
                          :items="resultTypeOptions"
                          item-text="name"
                          item-value="id"
                          :label="$t('wkt.admin.training-action.edit-window.result-type')"
                          :disabled="isReadOnly"
                          :menu-props="trainingActionDialogMenuProps"
                        />
                      </v-col>
                      <v-col cols="12" md="3" class="d-flex align-center">
                        <div>
                          <div style="font-size: 12px; color: #757575;">
                            {{ $t("wkt.admin.training-action.edit-window.calculated-status") }}
                          </div>
                          <v-chip :color="statusChip.color" small dark>
                            {{ statusChip.text }}
                          </v-chip>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <div style="font-size: 12px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #757575; margin-top: 8px;">
                          {{ $t("wkt.admin.training-action.edit-window.section.access-enrollments") }}
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="data.visibilityType"
                          :items="visibilityTypeOptions"
                          item-text="name"
                          item-value="id"
                          :label="$t('wkt.admin.training-action.edit-window.visibility-type')"
                          :disabled="isReadOnly"
                          :menu-props="trainingActionDialogMenuProps"
                          @change="onVisibilityTypeChanged"
                        />
                      </v-col>
                      <v-col cols="12" md="8" v-if="data.visibilityType !== 0">
                        <v-autocomplete
                          v-model="selectedVisibilityIds"
                          :items="visibilityItems"
                          :item-text="getVisibilityItemText"
                          item-value="id"
                          :label="visibilityValueLabel"
                          :rules="visibilitySelectionRules"
                          multiple
                          chips
                          deletable-chips
                          :disabled="isReadOnly"
                          :menu-props="trainingActionDialogMenuProps"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="!isCorporateLicense">
                      <v-col cols="12" md="8" :offset-md="4">
                        <span style="color: #757575; font-size: 13px;">
                          {{ $t("wkt.admin.training-action.edit-window.corporate-only-note") }}
                        </span>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="6" class="d-flex align-center">
                        <v-checkbox
                          v-model="allowAutomaticEnrollment"
                          :label="$t('wkt.admin.training-action.edit-window.allow-automatic-enrollment')"
                          :disabled="isReadOnly || !isCorporateLicense"
                        />
                      </v-col>
                      <v-col cols="12" md="6" class="d-flex align-center">
                        <v-checkbox
                          v-model="data.requiresApproval"
                          :label="$t('wkt.admin.training-action.edit-window.requires-approval')"
                          :disabled="isReadOnly || !isCorporateLicense"
                          @change="onRequiresApprovalChanged"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="data.requiresApproval">
                      <v-col cols="12">
                        <v-autocomplete
                          v-model="data.procedureDefinitionId"
                          :items="trainingProcedureDefinitions"
                          item-text="name"
                          item-value="id"
                          :label="$t('wkt.admin.training-action.edit-window.procedure-definition')"
                          :disabled="isReadOnly || !isCorporateLicense"
                          :menu-props="trainingActionDialogMenuProps"
                          clearable
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="data.isCanceled">
                      <v-col cols="12">
                        <v-alert dense outlined type="warning">
                          <div>
                            {{ $t("wkt.admin.training-action.edit-window.canceled-info") }}
                          </div>
                          <div v-if="data.cancellationDate" class="mt-1">
                            {{ $t("wkt.admin.training-action.edit-window.cancellation-date") }}:
                            {{ formatTrainingSimpleDate(data.cancellationDate) }}
                          </div>
                          <div v-if="data.cancellationReason" class="mt-1">
                            {{ $t("wkt.admin.training-action.edit-window.cancellation-reason") }}:
                            {{ data.cancellationReason }}
                          </div>
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tab-item>

                <v-tab-item>
                  <v-container fluid class="px-0 pt-4">
                    <v-row>
                      <v-col cols="12">
                        <div style="font-size: 12px; color: #757575; margin-bottom: 8px;">
                          {{ $t("wkt.admin.training-action.edit-window.objectives-program") }}
                        </div>
                        <TiptapVuetify
                          :vuetify-instance="$vuetify"
                          v-model="data.objectivesProgram"
                          :placeholder="actionObjectivesProgramPlaceholder"
                          :extensions="editorExtensions"
                          :editable="!isReadOnly"
                          :card-props="{ flat: true }"
                          @wheel="onWheel"
                          style="border: 1px solid rgba(0,0,0,.12); border-radius: 6px; min-height: 280px;"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="5">
                        <div style="font-size: 12px; color: #757575; margin-bottom: 8px;">
                          {{ $t("wkt.admin.training-action.edit-window.thumbnail") }}
                        </div>
                        <v-img
                          v-if="currentThumbnailUrl"
                          :src="currentThumbnailUrl"
                          max-height="180"
                          contain
                          style="border: 1px solid #E0E0E0; border-radius: 8px; background: #FAFAFA;"
                        />
                        <div
                          v-else
                          style="height: 180px; border: 1px dashed #D0D0D0; border-radius: 8px; background: #FAFAFA; display: flex; align-items: center; justify-content: center; color: #757575; text-align: center; padding: 16px;"
                        >
                          {{ $t("wkt.admin.training-action.edit-window.thumbnail-empty") }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="7" class="d-flex flex-column justify-center">
                        <div style="font-size: 13px; color: #757575; margin-bottom: 16px;">
                          {{
                            data.id
                              ? $t("wkt.admin.training-action.edit-window.thumbnail-help")
                              : $t("wkt.admin.training-action.edit-window.thumbnail-save-first")
                          }}
                        </div>
                        <div class="d-flex flex-wrap">
                          <v-btn
                            color="#185EA9"
                            text
                            class="mr-2 mb-2"
                            @click="uploadThumbnail()"
                            :disabled="isReadOnly || !data.id || loadingThumbnail"
                            style="font-size: 0.9em; font-weight: 400;"
                          >
                            <v-icon left color="#185EA9">insert_photo</v-icon>
                            {{
                              $t(
                                data.thumbnailPath
                                  ? "wkt.admin.training-action.edit-window.thumbnail-replace"
                                  : "wkt.admin.training-action.edit-window.thumbnail-upload",
                              ).toUpperCase()
                            }}
                          </v-btn>
                          <v-btn
                            v-if="data.thumbnailPath"
                            color="#185EA9"
                            text
                            class="mb-2"
                            @click="deleteThumbnail()"
                            :disabled="isReadOnly || loadingThumbnail"
                            style="font-size: 0.9em; font-weight: 400;"
                          >
                            <v-icon left color="#185EA9">delete</v-icon>
                            {{ $t("wkt.admin.training-action.edit-window.thumbnail-delete").toUpperCase() }}
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tab-item>

                <v-tab-item>
                  <v-container fluid class="px-0 pt-4">
                    <v-row v-if="!isCorporateLicense">
                      <v-col cols="12">
                        <span style="color: #757575; font-size: 13px;">
                          {{ $t("wkt.admin.training-action.edit-window.corporate-only-note") }}
                        </span>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.totalCost"
                          :label="$t('wkt.admin.training-action.edit-window.total-cost')"
                          type="number"
                          step="0.01"
                          min="0"
                          :disabled="isReadOnly || !isCorporateLicense"
                        />
                      </v-col>
                      <v-col cols="12" md="4" class="d-flex align-center">
                        <v-checkbox
                          v-model="data.bonusEligible"
                          :label="$t('wkt.admin.training-action.edit-window.bonus-eligible')"
                          :disabled="isReadOnly || !isCorporateLicense"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="data.fundaeMode"
                          :items="fundaeModeOptions"
                          item-text="name"
                          item-value="id"
                          :label="$t('wkt.admin.training-action.edit-window.fundae-mode')"
                          :disabled="isReadOnly || !isCorporateLicense"
                          :menu-props="trainingActionDialogMenuProps"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.fundaeGroupCode"
                          :label="$t('wkt.admin.training-action.edit-window.fundae-group-code')"
                          maxlength="100"
                          counter="100"
                          :disabled="isReadOnly || !isCorporateLicense"
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.fundaeHours"
                          :label="$t('wkt.admin.training-action.edit-window.fundae-hours')"
                          type="number"
                          step="0.25"
                          min="0"
                          :disabled="isReadOnly || !isCorporateLicense"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="data.bonusCost"
                          :label="$t('wkt.admin.training-action.edit-window.bonus-cost')"
                          type="number"
                          step="0.01"
                          min="0"
                          :disabled="isReadOnly || !isCorporateLicense"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tab-item>

                <v-tab-item>
                  <v-container fluid class="px-0 pt-4">
                    <v-row v-if="!data.id">
                      <v-col cols="12">
                        <span style="color: #757575; font-size: 13px;">
                          {{ $t("wkt.admin.training-action.edit-window.enrollments-save-first") }}
                        </span>
                      </v-col>
                    </v-row>

                    <template v-else>
                      <v-row class="align-center">
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="enrollmentSearch"
                            :label="$t('wkt.admin.training-action.edit-window.enrollments-search')"
                            clearable
                            prepend-inner-icon="search"
                            :disabled="loadingEnrollments"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="enrollmentStatusFilter"
                            :items="enrollmentStatusOptions"
                            item-text="name"
                            item-value="id"
                            :label="$t('wkt.admin.training-action.edit-window.enrollments-filter-status')"
                            clearable
                            :disabled="loadingEnrollments"
                            :menu-props="trainingActionDialogMenuProps"
                          />
                        </v-col>
                      </v-row>

                      <v-row class="align-center">
                        <v-col cols="12" class="d-flex justify-end flex-wrap pt-0">
                          <v-btn
                            color="#185EA9"
                            text
                            class="mr-2 mb-2"
                            @click="openTrainingEnrollmentBatchDialog()"
                            :disabled="isReadOnly || data.isCanceled || loadingEnrollments"
                            style="font-size: 0.9em; font-weight: 400;"
                          >
                            <v-icon left color="#185EA9">group_add</v-icon>
                            {{ $t("wkt.admin.training-action.edit-window.add-enrollment").toUpperCase() }}
                          </v-btn>
                          <v-btn
                            color="#185EA9"
                            text
                            class="mr-2 mb-2"
                            @click="openTrainingEnrollmentResultBatchDialog()"
                            :disabled="isReadOnly || loadingEnrollments || filteredTrainingEnrollments.length === 0"
                            style="font-size: 0.9em; font-weight: 400;"
                          >
                            <v-icon left color="#185EA9">assignment_turned_in</v-icon>
                            {{ $t("wkt.admin.training-action.edit-window.register-results-batch").toUpperCase() }}
                          </v-btn>
                          <v-btn
                            color="#185EA9"
                            text
                            class="mb-2"
                            @click="openTrainingEnrollmentNotificationDialog()"
                            :disabled="isReadOnly || loadingEnrollments || filteredTrainingEnrollments.length === 0"
                            style="font-size: 0.9em; font-weight: 400;"
                          >
                            <v-icon left color="#185EA9">markunread_mailbox</v-icon>
                            {{ $t("wkt.admin.training-action.edit-window.send-notification").toUpperCase() }}
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-row class="mb-2">
                        <v-col cols="12">
                          <v-chip class="mr-2 mb-2" small>
                            {{ $t("wkt.admin.training-action.edit-window.enrollments-total") }}: {{ trainingEnrollments.length }}
                          </v-chip>
                          <v-chip class="mr-2 mb-2" small>
                            {{ $t("wkt.admin.training-enrollment.status.pending-approval") }}: {{ enrollmentStatusCount(0) }}
                          </v-chip>
                          <v-chip class="mr-2 mb-2" small>
                            {{ $t("wkt.admin.training-enrollment.status.pending-selection") }}: {{ enrollmentStatusCount(1) }}
                          </v-chip>
                          <v-chip class="mr-2 mb-2" small>
                            {{ $t("wkt.admin.training-enrollment.status.enrolled") }}: {{ enrollmentStatusCount(2) }}
                          </v-chip>
                          <v-chip class="mr-2 mb-2" small>
                            {{ $t("wkt.admin.training-action.table.headers.pending-results") }}: {{ enrollmentPendingResultsCount }}
                          </v-chip>
                        </v-col>
                      </v-row>

                      <v-data-table
                        :headers="trainingEnrollmentHeaders"
                        :items="filteredTrainingEnrollments"
                        :loading="loadingEnrollments"
                        :loading-text="$t('wkt.admin.common.table.loading')"
                        :no-data-text="$t('wkt.admin.common.table.no_data')"
                        :items-per-page="10"
                        class="elevation-0"
                      >
                        <template v-slot:[`item.employee`]="{ item }">
                          <div>
                            <strong>{{ getTrainingEnrollmentEmployeeName(item) }}</strong>
                            <div style="font-size: 12px; color: #757575;">
                              {{ item.employeeDepartmentName }}<span v-if="item.employeeDepartmentName && item.employeeWorkplaceName"> - </span>{{ item.employeeWorkplaceName }}
                            </div>
                          </div>
                        </template>
                        <template v-slot:[`item.status`]="{ item }">
                          <v-chip :color="getTrainingEnrollmentStatusColor(item.status)" small dark>
                            {{ getTrainingEnrollmentStatusText(item.status) }}
                          </v-chip>
                        </template>
                        <template v-slot:[`item.enrollmentDate`]="{ item }">
                          {{ formatTrainingSimpleDate(item.enrollmentDate) }}
                        </template>
                        <template v-slot:[`item.resultDate`]="{ item }">
                          {{ formatTrainingSimpleDate(item.resultDate) }}
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                          <v-tooltip v-if="data.manualSelection && item.status === 1" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="#2E7D32"
                                @click="openTrainingEnrollmentSelectionDialog(item, constants.TRAINING_ENROLLMENT_STATUS_ENROLLED)"
                                :disabled="isReadOnly"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>check_circle</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("wkt.admin.training-enrollment.actions.approve") }}</span>
                          </v-tooltip>
                          <v-tooltip v-if="data.manualSelection && item.status === 1" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="#C62828"
                                @click="openTrainingEnrollmentSelectionDialog(item, constants.TRAINING_ENROLLMENT_STATUS_REJECTED)"
                                :disabled="isReadOnly"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>highlight_off</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("wkt.admin.training-enrollment.actions.reject") }}</span>
                          </v-tooltip>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                @click="openTrainingEnrollmentDialog(item)"
                                :disabled="isReadOnly"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>edit</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("wkt.admin.training-enrollment.actions.edit") }}</span>
                          </v-tooltip>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                @click="uploadTrainingEnrollmentCertificate(item)"
                                :disabled="isReadOnly"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>attach_file</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("wkt.admin.training-enrollment.actions.upload-certificate") }}</span>
                          </v-tooltip>
                          <v-tooltip v-if="item.certificateDocumentId" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                @click="downloadTrainingEnrollmentCertificate(item)"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>description</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("wkt.admin.training-enrollment.actions.download-certificate") }}</span>
                          </v-tooltip>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                @click="openTrainingEnrollmentCancelDialog(item)"
                                :disabled="isReadOnly || item.status === 7"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>cancel</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("wkt.admin.training-enrollment.actions.cancel") }}</span>
                          </v-tooltip>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                @click="deleteTrainingEnrollment(item)"
                                :disabled="isReadOnly || !canDeleteTrainingEnrollment(item)"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon small>delete</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("wkt.admin.training-enrollment.actions.delete") }}</span>
                          </v-tooltip>
                        </template>
                      </v-data-table>
                    </template>
                  </v-container>
                </v-tab-item>
              </v-tabs-items>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="data.id"
            color="#ff0000"
            @click="confirmDelete()"
            :disabled="isReadOnly"
            style="font-size: 0.9em; font-weight: 400;"
          >
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{ $t('wkt.admin.common.dialog-buttons.delete').toUpperCase() }}</span>
          </v-btn>
          <v-btn
            v-if="data.id"
            color="#185EA9"
            text
            @click="duplicateTrainingAction()"
            :disabled="isReadOnly"
            style="font-size: 0.9em; font-weight: 400;"
          >
            <v-icon left color="#185EA9">content_copy</v-icon>
            {{ $t("wkt.admin.training-action.edit-window.duplicate").toUpperCase() }}
          </v-btn>
          <v-btn
            v-if="data.id && !data.isCanceled"
            color="#185EA9"
            text
            @click="openCancelActionDialog()"
            :disabled="isReadOnly"
            style="font-size: 0.9em; font-weight: 400;"
          >
            <v-icon left color="#185EA9">cancel</v-icon>
            {{ $t("wkt.admin.training-action.edit-window.cancel-action").toUpperCase() }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            :disabled="isReadOnly"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
          >
            {{ $t("wkt.admin.common.dialog-buttons.close").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      v-bind:title="$t('wkt.admin.training-action.edit-window.confirm-dialog.title')"
      v-bind:message="$t('wkt.admin.training-action.edit-window.confirm-dialog.text')"
      v-bind:titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      v-bind:titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      v-on:confirmed="deleteItem()"
    />
    <v-dialog v-model="showCancelDialog" max-width="650px">
      <v-card>
        <v-card-title>
          {{ $t("wkt.admin.training-action.edit-window.cancel-dialog.title") }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            {{ $t("wkt.admin.training-action.edit-window.cancel-dialog.text") }}
          </div>
          <v-textarea
            v-model="cancelReason"
            :label="$t('wkt.admin.training-action.edit-window.cancel-dialog.reason')"
            auto-grow
            rows="3"
            maxlength="1000"
            counter="1000"
            :disabled="cancelLoading"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="#EF6C00"
            text
            @click="confirmCancelAction()"
            :loading="cancelLoading"
            :disabled="cancelLoading"
          >
            {{ $t("wkt.admin.training-action.edit-window.cancel-dialog.confirm").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showCancelDialog = false"
            :disabled="cancelLoading"
          >
            {{ $t("wkt.admin.common.dialog-buttons.close").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showEnrollmentCancelDialog" max-width="650px">
      <v-card>
        <v-card-title>
          {{ $t("wkt.admin.training-enrollment.cancel-dialog.title") }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            {{ $t("wkt.admin.training-enrollment.cancel-dialog.text") }}
          </div>
          <v-textarea
            v-if="enrollmentCancelOriginalObservations"
            :value="enrollmentCancelOriginalObservations"
            :label="$t('wkt.admin.training-enrollment.cancel-dialog.original-observations')"
            auto-grow
            rows="3"
            readonly
            outlined
            class="mb-4"
          />
          <v-textarea
            v-model="enrollmentCancelReason"
            :label="$t('wkt.admin.training-enrollment.cancel-dialog.reason')"
            auto-grow
            rows="3"
            maxlength="2000"
            counter="2000"
            :disabled="enrollmentCancelLoading"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="#EF6C00"
            text
            @click="confirmCancelTrainingEnrollment()"
            :loading="enrollmentCancelLoading"
            :disabled="enrollmentCancelLoading"
          >
            {{ $t("wkt.admin.training-enrollment.cancel-dialog.confirm").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showEnrollmentCancelDialog = false"
            :disabled="enrollmentCancelLoading"
          >
            {{ $t("wkt.admin.common.dialog-buttons.close").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showEnrollmentSelectionDialog" max-width="650px">
      <v-card>
        <v-card-title>
          {{
            selectionDialogStatus === constants.TRAINING_ENROLLMENT_STATUS_ENROLLED
              ? $t("wkt.admin.training-enrollment.selection-dialog.approve-title")
              : $t("wkt.admin.training-enrollment.selection-dialog.reject-title")
          }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            {{
              selectionDialogStatus === constants.TRAINING_ENROLLMENT_STATUS_ENROLLED
                ? $t("wkt.admin.training-enrollment.selection-dialog.approve-text")
                : $t("wkt.admin.training-enrollment.selection-dialog.reject-text")
            }}
          </div>
          <v-textarea
            v-model="selectionDialogObservations"
            :label="$t('wkt.admin.training-enrollment.selection-dialog.observations')"
            auto-grow
            rows="3"
            maxlength="2000"
            counter="2000"
            :disabled="selectionDialogLoading"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :color="selectionDialogStatus === constants.TRAINING_ENROLLMENT_STATUS_ENROLLED ? '#2E7D32' : '#C62828'"
            text
            @click="confirmTrainingEnrollmentSelection()"
            :loading="selectionDialogLoading"
            :disabled="selectionDialogLoading"
          >
            {{
              selectionDialogStatus === constants.TRAINING_ENROLLMENT_STATUS_ENROLLED
                ? $t("wkt.admin.training-enrollment.selection-dialog.approve-confirm").toUpperCase()
                : $t("wkt.admin.training-enrollment.selection-dialog.reject-confirm").toUpperCase()
            }}
          </v-btn>
          <v-btn
            text
            @click="showEnrollmentSelectionDialog = false"
            :disabled="selectionDialogLoading"
          >
            {{ $t("wkt.admin.common.dialog-buttons.close").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <TrainingEnrollmentDialog
      ref="trainingEnrollmentDialog"
      v-on:save="saveTrainingEnrollment($event)"
    />
    <TrainingEnrollmentBatchDialog
      ref="trainingEnrollmentBatchDialog"
      v-on:save="saveTrainingEnrollmentBatch($event)"
    />
    <TrainingEnrollmentResultBatchDialog
      ref="trainingEnrollmentResultBatchDialog"
      v-on:save="saveTrainingEnrollmentResultsBatch($event)"
      v-on:show-alert="$emit('show-alert', $event)"
    />
    <TrainingNotificationDialog
      ref="trainingNotificationDialog"
      v-on:save="sendTrainingEnrollmentNotification($event)"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog";
import TrainingEnrollmentDialog from "./TrainingEnrollmentDialog.vue";
import TrainingEnrollmentBatchDialog from "./TrainingEnrollmentBatchDialog.vue";
import TrainingEnrollmentResultBatchDialog from "./TrainingEnrollmentResultBatchDialog.vue";
import TrainingNotificationDialog from "./TrainingNotificationDialog.vue";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt.js";
import { Constants } from "../../common/Constants.js";
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  History,
  Paragraph,
  HardBreak,
} from "tiptap-vuetify";

export default {
  name: "TrainingActionDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog,
    TrainingEnrollmentDialog,
    TrainingEnrollmentBatchDialog,
    TrainingEnrollmentResultBatchDialog,
    TrainingNotificationDialog,
    TiptapVuetify,
  },
  data() {
    return {
      showDialog: false,
      loading: true,
      loadingThumbnail: false,
      tab: 0,
      data: {},
      profile: null,
      company: null,
      trainingCategories: [],
      trainingCourses: [],
      trainingProcedureDefinitions: [],
      workplaces: [],
      departments: [],
      legalEntities: [],
      projects: [],
      employees: [],
      trainingEnrollments: [],
      loadingEnrollments: false,
      enrollmentSearch: "",
      enrollmentStatusFilter: null,
      enrollmentStartDate: null,
      enrollmentEndDate: null,
      realizationStartDate: null,
      realizationEndDate: null,
      enrollmentStartDateMenu: false,
      enrollmentEndDateMenu: false,
      realizationStartDateMenu: false,
      realizationEndDateMenu: false,
      enrollmentStartDateFormatted: null,
      enrollmentEndDateFormatted: null,
      realizationStartDateFormatted: null,
      realizationEndDateFormatted: null,
      enrollmentDateError: false,
      showCancelDialog: false,
      cancelReason: "",
      cancelLoading: false,
      showEnrollmentCancelDialog: false,
      enrollmentCancelOriginalObservations: "",
      enrollmentCancelReason: "",
      enrollmentCancelLoading: false,
      enrollmentToCancel: null,
      showEnrollmentSelectionDialog: false,
      selectionDialogStatus: null,
      selectionDialogObservations: "",
      selectionDialogLoading: false,
      enrollmentSelectionTarget: null,
      trainingActionDialogMenuProps: {
        zIndex: 260,
        contentClass: "training-action-dialog-menu",
      },
      selectedVisibilityIds: [],
      editorExtensions: [
        History,
        Link,
        Underline,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        Bold,
        Paragraph,
        HardBreak,
      ],
    };
  },
  computed: {
    sessionModuleTraining() {
      return this.profile?.moduleTraining;
    },
    userHasAnyAccess() {
      return this.sessionModuleTraining !== Constants.DENY_ALL;
    },
    isReadOnly() {
      return this.sessionModuleTraining === Constants.ALLOW_READ_ONLY;
    },
    isCorporateLicense() {
      return this.company?.licenseType === Constants.LICENSE_TYPE_CORPORATE;
    },
    currentThumbnailUrl() {
      if (!this.data || !this.data.thumbnailPath) {
        return null;
      }
      return this.getPubilcAssetUrl(this.data.thumbnailPath);
    },
    actionObjectivesProgramPlaceholder() {
      return this.$t("wkt.admin.training-action.edit-window.objectives-program-placeholder");
    },
    trainingCoursesWithEmptyOption() {
      const emptyOption = {
        id: null,
        courseLabel: this.$t("wkt.admin.training-action.edit-window.no-course"),
      };
      return [
        emptyOption,
        ...(this.trainingCourses || []).map((course) => ({
          ...course,
          courseLabel: course.reference
            ? `${course.reference} - ${course.name}`
            : course.name,
        })),
      ];
    },
    modeOptions() {
      return [
        { id: 0, name: this.$t("wkt.admin.training-action.mode.presential") },
        { id: 1, name: this.$t("wkt.admin.training-action.mode.online") },
        { id: 2, name: this.$t("wkt.admin.training-action.mode.mixed") },
      ];
    },
    resultTypeOptions() {
      return [
        { id: 0, name: this.$t("wkt.admin.training-action.result-type.pass-fail") },
        { id: 1, name: this.$t("wkt.admin.training-action.result-type.numeric-grade") },
        { id: 2, name: this.$t("wkt.admin.training-action.result-type.attendance-only") },
      ];
    },
    allowAutomaticEnrollment: {
      get() {
        return !this.data?.manualSelection;
      },
      set(value) {
        this.data.manualSelection = !value;
      },
    },
    fundaeModeOptions() {
      return [
        { id: 0, name: this.$t("wkt.admin.training-action.fundae-mode.presential") },
        { id: 1, name: this.$t("wkt.admin.training-action.fundae-mode.virtual") },
        { id: 2, name: this.$t("wkt.admin.training-action.fundae-mode.mixed") },
      ];
    },
    visibilityTypeOptions() {
      return [
        { id: 0, name: this.$t("wkt.admin.training-action.visibility-type.company") },
        { id: 1, name: this.$t("wkt.admin.training-action.visibility-type.legal-entity") },
        { id: 2, name: this.$t("wkt.admin.training-action.visibility-type.department") },
        { id: 3, name: this.$t("wkt.admin.training-action.visibility-type.workplace") },
        { id: 4, name: this.$t("wkt.admin.training-action.visibility-type.project") },
        { id: 5, name: this.$t("wkt.admin.training-action.visibility-type.employee") },
      ];
    },
    statusChip() {
      const today = new Date().toISOString().split("T")[0];
      if (this.data.isCanceled) {
        return {
          text: this.$t("wkt.admin.training-action.status.canceled"),
          color: "#757575",
        };
      }
      if (this.realizationEndDate && this.realizationEndDate < today) {
        return {
          text: this.$t("wkt.admin.training-action.status.finished"),
          color: "#455A64",
        };
      }
      if (
        this.realizationStartDate &&
        this.realizationStartDate <= today &&
        (!this.realizationEndDate || this.realizationEndDate >= today)
      ) {
        return {
          text: this.$t("wkt.admin.training-action.status.in-progress"),
          color: "#2E7D32",
        };
      }
      if (
        this.enrollmentEndDate &&
        this.enrollmentEndDate < today &&
        (!this.realizationStartDate || this.realizationStartDate > today)
      ) {
        return {
          text: this.$t("wkt.admin.training-action.status.closed"),
          color: "#EF6C00",
        };
      }
      if (
        this.enrollmentStartDate &&
        this.enrollmentStartDate <= today &&
        (!this.enrollmentEndDate || this.enrollmentEndDate >= today)
      ) {
        return {
          text: this.$t("wkt.admin.training-action.status.open"),
          color: "#1565C0",
        };
      }
      return {
        text: this.$t("wkt.admin.training-action.status.planned"),
        color: "#5E35B1",
      };
    },
    visibilityItems() {
      switch (this.data.visibilityType) {
        case 1:
          return this.legalEntities;
        case 2:
          return this.departments;
        case 3:
          return this.workplaces;
        case 4:
          return this.projects;
        case 5:
          return this.employees;
        default:
          return [];
      }
    },
    visibilityValueLabel() {
      switch (this.data.visibilityType) {
        case 1:
          return this.$t("wkt.admin.training-action.edit-window.visibility-legal-entities");
        case 2:
          return this.$t("wkt.admin.training-action.edit-window.visibility-departments");
        case 3:
          return this.$t("wkt.admin.training-action.edit-window.visibility-workplaces");
        case 4:
          return this.$t("wkt.admin.training-action.edit-window.visibility-projects");
        case 5:
          return this.$t("wkt.admin.training-action.edit-window.visibility-employees");
        default:
          return "";
      }
    },
    visibilitySelectionRules() {
      return this.data.visibilityType !== 0
        ? [this.validationRules.requiredMultiple]
        : [];
    },
    trainingEnrollmentHeaders() {
      return [
        {
          text: this.$t("wkt.admin.training-enrollment.table.headers.worker"),
          value: "employee",
          sortable: false,
        },
        {
          text: this.$t("wkt.admin.training-enrollment.table.headers.status"),
          value: "status",
        },
        {
          text: this.$t("wkt.admin.training-enrollment.table.headers.enrollment-date"),
          value: "enrollmentDate",
        },
        {
          text: this.$t("wkt.admin.training-enrollment.table.headers.result-value"),
          value: "resultValue",
          sortable: false,
        },
        {
          text: this.$t("wkt.admin.training-enrollment.table.headers.result-date"),
          value: "resultDate",
        },
        {
          text: this.$t("wkt.admin.training-enrollment.table.headers.actions"),
          value: "actions",
          sortable: false,
          align: "end",
        },
      ];
    },
    enrollmentStatusOptions() {
      return [
        { id: 0, name: this.$t("wkt.admin.training-enrollment.status.pending-approval") },
        { id: 1, name: this.$t("wkt.admin.training-enrollment.status.pending-selection") },
        { id: 2, name: this.$t("wkt.admin.training-enrollment.status.enrolled") },
        { id: 3, name: this.$t("wkt.admin.training-enrollment.status.rejected") },
        { id: 4, name: this.$t("wkt.admin.training-enrollment.status.completed") },
        { id: 5, name: this.$t("wkt.admin.training-enrollment.status.not-pass") },
        { id: 6, name: this.$t("wkt.admin.training-enrollment.status.no-show") },
        { id: 7, name: this.$t("wkt.admin.training-enrollment.status.canceled") },
      ];
    },
    filteredTrainingEnrollments() {
      const search = (this.enrollmentSearch || "").trim().toLowerCase();
      return (this.trainingEnrollments || []).filter((item) => {
        if (!item) {
          return false;
        }
        if (this.enrollmentStatusFilter !== null && this.enrollmentStatusFilter !== undefined) {
          if (item.status !== this.enrollmentStatusFilter) {
            return false;
          }
        }
        if (!search) {
          return true;
        }
        const haystack = [
          this.getTrainingEnrollmentEmployeeName(item),
          item.employeeEmail,
          item.resultValue,
          item.observations,
          item.cancellationReason,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(search);
      });
    },
    enrollmentPendingResultsCount() {
      return (this.trainingEnrollments || []).filter(
        (item) =>
          item &&
          item.status === Constants.TRAINING_ENROLLMENT_STATUS_ENROLLED &&
          (!item.resultValue || item.resultValue.trim() === "") &&
          !item.resultDate &&
          this.realizationEndDate &&
          this.realizationEndDate < new Date().toISOString().split("T")[0],
      ).length;
    },
  },
  mounted() {
    this.installShadowMenuWheelPatch({
      menuSelector: ".training-action-dialog-menu",
      handlerKey: "__tweemTrainingActionManualWheelHandler",
    });
  },
  beforeDestroy() {
    this.teardownShadowMenuWheelPatch();
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return;
      }

      event.stopPropagation();
    },
    // eslint-disable-next-line no-unused-vars
    open(action, payload) {
      this.profile = gwt.getSessionProfile();
      this.company = gwt.getSessionInformation()?.company || null;

      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.common.error.try_again_later"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.showDialog = true;
      this.tab = 0;
      this.trainingEnrollments = [];
      this.enrollmentSearch = "";
      this.enrollmentStatusFilter = null;
      this.showCancelDialog = false;
      this.cancelReason = "";
      this.cancelLoading = false;
      this.showEnrollmentCancelDialog = false;
      this.enrollmentCancelOriginalObservations = "";
      this.enrollmentCancelReason = "";
      this.enrollmentCancelLoading = false;
      this.enrollmentToCancel = null;
      this.showEnrollmentSelectionDialog = false;
      this.selectionDialogStatus = null;
      this.selectionDialogObservations = "";
      this.selectionDialogLoading = false;
      this.enrollmentSelectionTarget = null;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.loading = true;
      this.loadDependencies(() => {
        if (!payload) {
          this.data = this.getDefaultData();
          this.syncDateInputs();
          this.syncVisibilitySelection();
          this.loading = false;
          return;
        }

        api.getTrainingActionById(
          payload,
          (res) => {
          this.data = {
              ...this.getDefaultData(),
              ...res,
              objectivesProgram: res && res.objectivesProgram ? res.objectivesProgram : "",
            };
            this.syncDateInputs();
            this.syncVisibilitySelection();
            this.cancelReason = this.data.cancellationReason || "";
            this.loadTrainingEnrollments(() => {
              this.loading = false;
            });
          },
          (error) => {
            this.loading = false;
            this.$emit("show-alert", {
              text: error && error.message ? error.message : error,
              color: "error",
              timeout: 3000,
            });
          },
        );
      });
    },
    getDefaultData() {
      return {
        companyId: this.profile?.companyId || null,
        courseId: null,
        reference: "",
        name: "",
        description: "",
        objectivesProgram: "",
        thumbnailPath: null,
        trainingCategoryId: null,
        mode: null,
        durationHours: null,
        instructorText: "",
        locationText: "",
        scheduleText: "",
        minSeats: null,
        maxSeats: null,
        visibilityType: 0,
        visibilityIds: null,
        resultType: 0,
        requiresApproval: false,
        procedureDefinitionId: null,
        manualSelection: !this.isCorporateLicense,
        totalCost: null,
        bonusEligible: false,
        fundaeMode: null,
        fundaeGroupCode: "",
        fundaeHours: null,
        bonusCost: null,
        isCanceled: false,
        cancellationDate: null,
        cancellationReason: null,
      };
    },
    loadDependencies(callback) {
      this.loadTargetData();
      this.loadTrainingCategories(() => {
        this.loadTrainingCourses(() => {
          this.loadProcedureDefinitions(callback);
        });
      });
    },
    loadTrainingCategories(callback) {
      api.getTrainingCategories(
        (res) => {
          this.trainingCategories = res || [];
          callback();
        },
        (error) => {
          this.handleAsyncError(error);
        },
      );
    },
    loadTrainingCourses(callback) {
      api.getTrainingCourses(
        (res) => {
          this.trainingCourses = res || [];
          callback();
        },
        (error) => {
          this.handleAsyncError(error);
        },
      );
    },
    loadProcedureDefinitions(callback) {
      api.getProcedureDefinitions(
        (res) => {
          this.trainingProcedureDefinitions = (res || []).filter(
            (item) => item && item.procedureClass === "TRAINING_ENROLLMENT",
          );
          callback();
        },
        (error) => {
          this.handleAsyncError(error);
        },
      );
    },
    loadTrainingEnrollments(callback) {
      if (!this.data || !this.data.id) {
        this.trainingEnrollments = [];
        if (callback) {
          callback();
        }
        return;
      }

      this.loadingEnrollments = true;
      api.getTrainingActionEnrollments(
        this.data.id,
        (res) => {
          this.trainingEnrollments = res || [];
          this.loadingEnrollments = false;
          if (callback) {
            callback();
          }
        },
        (error) => {
          this.loadingEnrollments = false;
          this.handleAsyncError(error);
        },
      );
    },
    loadTargetData() {
      this.workplaces = gwt.getWorkplacesByProfile();
      if (!this.workplaces || this.workplaces.length === 0) {
        api.getWorkPlaces(
          (data) => {
            this.workplaces = data || [];
          },
          () => {},
        );
      }

      this.departments = gwt.getDepartmentsByProfile();
      if (!this.departments || this.departments.length === 0) {
        api.getStaff(
          (data) => {
            this.departments = data || [];
            this.loadEmployeesFromDepartments();
          },
          () => {},
        );
      } else {
        this.loadEmployeesFromDepartments();
      }

      this.legalEntities = this.filterLegalEntitiesByProfile(gwt.getLegalEntities() || []);
      if (!this.legalEntities || this.legalEntities.length === 0) {
        api.getLegalEntities(
          (data) => {
            this.legalEntities = this.filterLegalEntitiesByProfile(data || []);
          },
          () => {},
        );
      }

      api.getProjects(
        (data) => {
          this.projects = this.filterProjectsByProfile(data || []);
        },
        () => {},
      );
    },
    filterLegalEntitiesByProfile(items) {
      const profile = gwt.getSessionProfile();
      if (!profile || !profile.legalEntities || profile.legalEntities.trim() === "") {
        return items;
      }
      const allowedIds = profile.legalEntities.split(",").map((id) => id.trim());
      return items.filter((item) => allowedIds.includes(String(item.id)));
    },
    filterProjectsByProfile(items) {
      const profile = gwt.getSessionProfile();
      if (!profile || !profile.projects || profile.projects.trim() === "") {
        return items;
      }
      const allowedIds = profile.projects.split(",").map((id) => id.trim());
      return items.filter((item) => allowedIds.includes(String(item.id)));
    },
    loadEmployeesFromDepartments() {
      const allEmployees = [];
      (this.departments || []).forEach((department) => {
        if (department && Array.isArray(department.employees)) {
          allEmployees.push(...department.employees);
        }
      });

      const profile = gwt.getSessionProfile();
      let allowedDepartments = [];
      let allowedWorkplaces = [];
      let allowedLegalEntities = [];
      let onlyMyTeam = false;

      if (profile) {
        if (profile.departments) {
          allowedDepartments = profile.departments.split(",").map(Number);
        }
        if (profile.workplaces) {
          allowedWorkplaces = profile.workplaces.split(",").map(Number);
        }
        if (profile.legalEntities) {
          allowedLegalEntities = profile.legalEntities.split(",").map(Number);
        }
        onlyMyTeam = !!profile.onlyMyTeam;
      }

      const myTeamIds = onlyMyTeam && gwt.getSessionInformation()?.myTeam
        ? gwt.getSessionInformation().myTeam.map((item) => item.id)
        : [];

      this.employees = allEmployees
        .filter((employee) => {
          const byDepartment =
            allowedDepartments.length === 0 ||
            allowedDepartments.includes(employee.departmentId);
          const byWorkplace =
            allowedWorkplaces.length === 0 ||
            allowedWorkplaces.includes(employee.workplaceId);
          const byLegalEntity =
            allowedLegalEntities.length === 0 ||
            allowedLegalEntities.includes(employee.legalEntityId);
          const byMyTeam = !onlyMyTeam || myTeamIds.includes(employee.id);
          return byDepartment && byWorkplace && byLegalEntity && byMyTeam;
        })
        .sort((a, b) => {
          const nameA = `${a.lastname || ""}, ${a.firstname || ""}`.toLowerCase();
          const nameB = `${b.lastname || ""}, ${b.firstname || ""}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
    },
    onCourseChanged(courseId) {
      if (!courseId) {
        return;
      }

      const course = this.trainingCourses.find((item) => item.id === courseId);
      if (!course) {
        return;
      }

      if (!this.data.name) {
        this.data.name = course.name;
      }
      if (!this.data.description) {
        this.data.description = course.description;
      }
      if (!this.data.objectivesProgram) {
        this.data.objectivesProgram = course.objectivesProgram || "";
      }
      if (!this.data.trainingCategoryId) {
        this.data.trainingCategoryId = course.trainingCategoryId;
      }
      if (this.data.mode === null || this.data.mode === undefined) {
        this.data.mode = course.defaultMode;
      }
      if (this.data.durationHours === null || this.data.durationHours === undefined || this.data.durationHours === "") {
        this.data.durationHours = course.estimatedDurationHours;
      }
    },
    onRequiresApprovalChanged(value) {
      if (!value) {
        this.data.procedureDefinitionId = null;
        return;
      }
      if (!this.data.procedureDefinitionId && this.trainingProcedureDefinitions.length > 0) {
        this.data.procedureDefinitionId = this.trainingProcedureDefinitions[0].id;
      }
    },
    syncDateInputs() {
      this.enrollmentStartDate = this.simpleDateToIso(this.data.enrollmentStart);
      this.enrollmentEndDate = this.simpleDateToIso(this.data.enrollmentEnd);
      this.realizationStartDate = this.simpleDateToIso(this.data.realizationStart);
      this.realizationEndDate = this.simpleDateToIso(this.data.realizationEnd);
    },
    syncVisibilitySelection() {
      this.selectedVisibilityIds = this.data.visibilityIds
        ? this.data.visibilityIds.split(",").map((id) => Number(id))
        : [];
    },
    onVisibilityTypeChanged() {
      this.selectedVisibilityIds = [];
      this.data.visibilityIds = null;
    },
    reloadTrainingAction(callback) {
      if (!this.data || !this.data.id) {
        if (callback) {
          callback();
        }
        return;
      }

      api.getTrainingActionById(
        this.data.id,
        (res) => {
          this.data = {
            ...this.getDefaultData(),
            ...res,
            objectivesProgram: res && res.objectivesProgram ? res.objectivesProgram : "",
          };
          this.syncDateInputs();
          this.syncVisibilitySelection();
          this.cancelReason = this.data.cancellationReason || "";
          this.loadTrainingEnrollments(() => {
            if (callback) {
              callback();
            }
          });
        },
        (error) => {
          if (callback) {
            callback();
          }
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    simpleDateToIso(value) {
      const date = this.convertSimpleDateToDate(value);
      return date ? this.formatLocalDateToISOStringDate(date).split(", ")[0] : null;
    },
    isoDateToSimpleDate(value) {
      return value ? this.convertDateToSimpleDate(new Date(`${value}`)) : null;
    },
    onSave() {
      this.enrollmentDateError = false;

      if (!this.enrollmentStartDate || !this.enrollmentEndDate) {
        this.enrollmentDateError = true;
        this.tab = 1;
        return;
      }

      if (this.data.visibilityType !== 0 && (!this.selectedVisibilityIds || this.selectedVisibilityIds.length === 0)) {
        this.tab = 1;
        this.$refs.form.validate();
        return;
      }

      if (!this.$refs.form.validate()) {
        this.tab = 0;
        return;
      }

      this.loading = true;

      this.data.enrollmentStart = this.isoDateToSimpleDate(this.enrollmentStartDate);
      this.data.enrollmentEnd = this.isoDateToSimpleDate(this.enrollmentEndDate);
      this.data.realizationStart = this.isoDateToSimpleDate(this.realizationStartDate);
      this.data.realizationEnd = this.isoDateToSimpleDate(this.realizationEndDate);
      this.data.visibilityIds =
        this.data.visibilityType === 0 || this.selectedVisibilityIds.length === 0
          ? null
          : this.selectedVisibilityIds.join(",");

      this.data.durationHours = this.normalizeNumber(this.data.durationHours);
      this.data.totalCost = this.normalizeNumber(this.data.totalCost);
      this.data.fundaeHours = this.normalizeNumber(this.data.fundaeHours);
      this.data.bonusCost = this.normalizeNumber(this.data.bonusCost);
      this.data.minSeats = this.normalizeInteger(this.data.minSeats);
      this.data.maxSeats = this.normalizeInteger(this.data.maxSeats);

      if (!this.isCorporateLicense) {
        this.data.manualSelection = true;
        this.data.requiresApproval = false;
        this.data.procedureDefinitionId = null;
        this.data.totalCost = null;
        this.data.bonusEligible = false;
        this.data.fundaeMode = null;
        this.data.fundaeGroupCode = null;
        this.data.fundaeHours = null;
        this.data.bonusCost = null;
      } else if (!this.data.requiresApproval) {
        this.data.procedureDefinitionId = null;
      }

      api.storeTrainingAction(
        this.data,
        (res) => {
          this.loading = false;
          if (res.rsOK) {
            this.showDialog = false;
            this.$emit("refresh");
          } else {
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    normalizeNumber(value) {
      return value === "" || value === null || value === undefined
        ? null
        : Number(value);
    },
    normalizeInteger(value) {
      return value === "" || value === null || value === undefined
        ? null
        : parseInt(value, 10);
    },
    duplicateTrainingAction() {
      const duplicatedData = {
        ...this.data,
        id: null,
        reference: "",
        isCanceled: false,
        cancellationDate: null,
        cancellationReason: null,
      };

      this.data = {
        ...this.getDefaultData(),
        ...duplicatedData,
        objectivesProgram: duplicatedData.objectivesProgram || "",
      };
      this.trainingEnrollments = [];
      this.enrollmentSearch = "";
      this.enrollmentStatusFilter = null;
      this.showCancelDialog = false;
      this.cancelReason = "";
      this.cancelLoading = false;
      this.showEnrollmentCancelDialog = false;
      this.enrollmentCancelOriginalObservations = "";
      this.enrollmentCancelReason = "";
      this.enrollmentCancelLoading = false;
      this.enrollmentToCancel = null;
      this.showEnrollmentSelectionDialog = false;
      this.selectionDialogStatus = null;
      this.selectionDialogObservations = "";
      this.selectionDialogLoading = false;
      this.enrollmentSelectionTarget = null;
      this.tab = 0;
      this.syncDateInputs();
      this.syncVisibilitySelection();
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    openCancelActionDialog() {
      this.cancelReason = this.data.cancellationReason || "";
      this.showCancelDialog = true;
    },
    confirmCancelAction() {
      if (!this.data || !this.data.id) {
        return;
      }

      this.cancelLoading = true;
      api.cancelTrainingAction(
        {
          id: this.data.id,
          cancellationReason: this.cancelReason,
        },
        (res) => {
          if (!res.rsOK) {
            this.cancelLoading = false;
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
            return;
          }

          this.reloadTrainingAction(() => {
            this.cancelLoading = false;
            this.showCancelDialog = false;
            this.tab = 4;
            this.$emit("show-alert", {
              text: this.$t("wkt.admin.training-action.edit-window.cancel-success"),
              color: "success",
              timeout: 3000,
            });
          });
        },
        (error) => {
          this.cancelLoading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    openTrainingEnrollmentDialog(item) {
      this.$refs.trainingEnrollmentDialog.open({
        enrollment: item || null,
        employees: this.employees,
        trainingAction: this.data,
        isReadOnly: this.isReadOnly,
      });
    },
    openTrainingEnrollmentBatchDialog() {
      this.$refs.trainingEnrollmentBatchDialog.open({
        employees: this.employees,
        trainingAction: this.data,
        isReadOnly: this.isReadOnly,
      });
    },
    openTrainingEnrollmentResultBatchDialog() {
      this.$refs.trainingEnrollmentResultBatchDialog.open({
        enrollments: this.filteredTrainingEnrollments,
        trainingAction: this.data,
        isReadOnly: this.isReadOnly,
      });
    },
    openTrainingEnrollmentNotificationDialog() {
      this.$refs.trainingNotificationDialog.open({
        enrollments: this.filteredTrainingEnrollments,
        trainingAction: this.data,
        isReadOnly: this.isReadOnly,
      });
    },
    saveTrainingEnrollment(payload) {
      this.loadingEnrollments = true;
      api.storeTrainingEnrollment(
        payload,
        (res) => {
          if (res.rsOK) {
            this.loadTrainingEnrollments(() => {});
            return;
          }
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    saveTrainingEnrollmentBatch(payload) {
      this.loadingEnrollments = true;
      api.storeTrainingEnrollmentBatch(
        payload,
        (res) => {
          if (res.rsOK) {
            this.loadTrainingEnrollments(() => {});
            this.$emit("show-alert", {
              text: res.message || this.$t("wkt.admin.training-enrollment-batch.window.success"),
              color: "success",
              timeout: 3000,
            });
            return;
          }
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    saveTrainingEnrollmentResultsBatch(payload) {
      this.loadingEnrollments = true;
      api.storeTrainingEnrollmentResultsBatch(
        payload,
        (res) => {
          if (res.rsOK) {
            this.loadTrainingEnrollments(() => {});
            this.$emit("show-alert", {
              text: res.message || this.$t("wkt.admin.training-enrollment-results-batch.window.success"),
              color: "success",
              timeout: 3000,
            });
            return;
          }
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    sendTrainingEnrollmentNotification(payload) {
      this.loadingEnrollments = true;
      api.sendTrainingEnrollmentNotification(
        payload,
        (res) => {
          this.loadingEnrollments = false;
          if (res.rsOK) {
            this.$emit("show-alert", {
              text: res.message || this.$t("wkt.admin.training-notification.window.success"),
              color: "success",
              timeout: 3000,
            });
            return;
          }
          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    setTrainingEnrollmentCanceled(item) {
      this.saveTrainingEnrollment({
        id: item.id,
        trainingActionId: item.trainingActionId,
        employeeId: item.employeeId,
        status: Constants.TRAINING_ENROLLMENT_STATUS_CANCELED,
        observations: item.observations,
        cancellationReason: item.cancellationReason,
        resultValue: item.resultValue,
        resultDate: item.resultDate,
        enrollmentDate: item.enrollmentDate,
        certificateDocumentId: item.certificateDocumentId,
        procedureRequestId: item.procedureRequestId,
      });
    },
    openTrainingEnrollmentCancelDialog(item) {
      this.enrollmentToCancel = item;
      this.enrollmentCancelOriginalObservations = item?.observations || "";
      this.enrollmentCancelReason = item?.cancellationReason || "";
      this.showEnrollmentCancelDialog = true;
    },
    confirmCancelTrainingEnrollment() {
      if (!this.enrollmentToCancel) {
        return;
      }

      this.enrollmentCancelLoading = true;
      const payload = {
        id: this.enrollmentToCancel.id,
        trainingActionId: this.enrollmentToCancel.trainingActionId,
        employeeId: this.enrollmentToCancel.employeeId,
        status: Constants.TRAINING_ENROLLMENT_STATUS_CANCELED,
        observations: this.enrollmentToCancel.observations,
        cancellationReason: this.enrollmentCancelReason,
        resultValue: this.enrollmentToCancel.resultValue,
        resultDate: this.enrollmentToCancel.resultDate,
        enrollmentDate: this.enrollmentToCancel.enrollmentDate,
        certificateDocumentId: this.enrollmentToCancel.certificateDocumentId,
        procedureRequestId: this.enrollmentToCancel.procedureRequestId,
      };

      api.storeTrainingEnrollment(
        payload,
        (res) => {
          if (res.rsOK) {
            this.loadTrainingEnrollments(() => {
              this.enrollmentCancelLoading = false;
              this.showEnrollmentCancelDialog = false;
              this.enrollmentCancelOriginalObservations = "";
              this.enrollmentCancelReason = "";
              this.enrollmentToCancel = null;
              this.$emit("show-alert", {
                text: this.$t("wkt.admin.training-enrollment.cancel-dialog.success"),
                color: "success",
                timeout: 3000,
              });
            });
            return;
          }

          this.enrollmentCancelLoading = false;
          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.enrollmentCancelLoading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    openTrainingEnrollmentSelectionDialog(item, status) {
      this.enrollmentSelectionTarget = item;
      this.selectionDialogStatus = status;
      this.selectionDialogObservations = item?.observations || "";
      this.selectionDialogLoading = false;
      this.showEnrollmentSelectionDialog = true;
    },
    confirmTrainingEnrollmentSelection() {
      if (!this.enrollmentSelectionTarget || this.selectionDialogStatus === null || this.selectionDialogStatus === undefined) {
        return;
      }

      const isRejected = this.selectionDialogStatus === Constants.TRAINING_ENROLLMENT_STATUS_REJECTED;
      const payload = {
        id: this.enrollmentSelectionTarget.id,
        trainingActionId: this.enrollmentSelectionTarget.trainingActionId,
        employeeId: this.enrollmentSelectionTarget.employeeId,
        status: this.selectionDialogStatus,
        observations: this.selectionDialogObservations,
        resultValue: isRejected ? null : this.enrollmentSelectionTarget.resultValue,
        resultDate: isRejected ? null : this.enrollmentSelectionTarget.resultDate,
        enrollmentDate: this.enrollmentSelectionTarget.enrollmentDate,
        certificateDocumentId: this.enrollmentSelectionTarget.certificateDocumentId,
        procedureRequestId: this.enrollmentSelectionTarget.procedureRequestId,
      };

      this.selectionDialogLoading = true;
      api.storeTrainingEnrollment(
        payload,
        (res) => {
          if (res.rsOK) {
            this.loadTrainingEnrollments(() => {
              this.selectionDialogLoading = false;
              this.showEnrollmentSelectionDialog = false;
              this.enrollmentSelectionTarget = null;
              this.selectionDialogStatus = null;
              this.selectionDialogObservations = "";
              this.$emit("show-alert", {
                text: this.$t(
                  isRejected
                    ? "wkt.admin.training-enrollment.selection-dialog.reject-success"
                    : "wkt.admin.training-enrollment.selection-dialog.approve-success",
                ),
                color: "success",
                timeout: 3000,
              });
            });
            return;
          }

          this.selectionDialogLoading = false;
          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.selectionDialogLoading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    deleteTrainingEnrollment(item) {
      this.loadingEnrollments = true;
      api.deleteTrainingEnrollment(
        item.id,
        (res) => {
          if (res.rsOK) {
            this.loadTrainingEnrollments(() => {});
            return;
          }
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.loadingEnrollments = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    uploadThumbnail() {
      if (!this.data || !this.data.id || this.isReadOnly) {
        return;
      }

      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (event) => {
        const file = event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;
        if (!file) {
          return;
        }

        const formData = new FormData();
        formData.append("file", file, file.name);

        this.loadingThumbnail = true;
        api.uploadTrainingActionThumbnail(
          this.data.id,
          formData,
          (res) => {
            this.loadingThumbnail = false;
            this.data.thumbnailPath = res;
            this.$emit("show-alert", {
              text: this.$t("wkt.admin.training-action.edit-window.thumbnail-upload-success"),
              color: "success",
              timeout: 3000,
            });
          },
          (error) => {
            this.loadingThumbnail = false;
            this.$emit("show-alert", {
              text: error && error.message ? error.message : error,
              color: "error",
              timeout: 3000,
            });
          },
        );
      };

      input.click();
    },
    deleteThumbnail() {
      if (!this.data || !this.data.id || !this.data.thumbnailPath || this.isReadOnly) {
        return;
      }

      this.loadingThumbnail = true;
      api.deleteTrainingActionThumbnail(
        this.data.id,
        (res) => {
          this.loadingThumbnail = false;
          if (res.rsOK) {
            this.data.thumbnailPath = null;
            this.$emit("show-alert", {
              text: this.$t("wkt.admin.training-action.edit-window.thumbnail-delete-success"),
              color: "success",
              timeout: 3000,
            });
            return;
          }

          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.loadingThumbnail = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    uploadTrainingEnrollmentCertificate(item) {
      if (!item || !item.id || this.isReadOnly) {
        return;
      }

      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".pdf,.png,.jpg,.jpeg,.doc,.docx";
      input.onchange = (event) => {
        const file = event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;
        if (!file) {
          return;
        }

        this.loadingEnrollments = true;
        this.buildTrainingEnrollmentCertificatePayload(
          file,
          (payload) => {
            api.uploadTrainingEnrollmentCertificate(
              item.id,
              payload,
              (res) => {
                if (res.rsOK) {
                  this.loadTrainingEnrollments(() => {});
                  this.$emit("show-alert", {
                    text: res.message,
                    color: "success",
                    timeout: 3000,
                  });
                  return;
                }

                this.loadingEnrollments = false;
                this.$emit("show-alert", {
                  text: res.message,
                  color: "error",
                  timeout: 3000,
                });
              },
              (error) => {
                this.loadingEnrollments = false;
                this.$emit("show-alert", {
                  text: error && error.message ? error.message : error,
                  color: "error",
                  timeout: 3000,
                });
              },
            );
          },
          (error) => {
            this.loadingEnrollments = false;
            this.$emit("show-alert", {
              text: error && error.message ? error.message : error,
              color: "error",
              timeout: 3000,
            });
          },
        );
      };

      input.click();
    },
    buildTrainingEnrollmentCertificatePayload(file, onSuccess, onError) {
      if (!file) {
        onError(new Error("Certificate file is required"));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const rawResult = typeof reader.result === "string" ? reader.result : "";
        const contentBase64 = rawResult.includes(",") ? rawResult.split(",")[1] : rawResult;

        onSuccess({
          fileName: file.name,
          contentBase64: contentBase64,
        });
      };
      reader.onerror = () => {
        onError(new Error("Unable to read certificate file"));
      };
      reader.readAsDataURL(file);
    },
    downloadTrainingEnrollmentCertificate(item) {
      if (!item || !item.id || !item.certificateDocumentId) {
        return;
      }

      api.downloadTrainingEnrollmentCertificate(
        item.id,
        null,
        (error) => {
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    canDeleteTrainingEnrollment(item) {
      return !item.procedureRequestId
        && !item.certificateDocumentId
        && !item.resultDate
        && (!item.resultValue || item.resultValue.trim() === "");
    },
    getTrainingEnrollmentEmployeeName(item) {
      if (!item) {
        return "";
      }
      return `${item.employeeLastname || ""}, ${item.employeeFirstname || ""}`.replace(/^,\s*/, "");
    },
    getTrainingEnrollmentStatusText(status) {
      const match = this.enrollmentStatusOptions.find((item) => item.id === status);
      return match ? match.name : "";
    },
    getTrainingEnrollmentStatusColor(status) {
      switch (status) {
        case Constants.TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL:
          return "#EF6C00";
        case Constants.TRAINING_ENROLLMENT_STATUS_PENDING_SELECTION:
          return "#6A1B9A";
        case Constants.TRAINING_ENROLLMENT_STATUS_ENROLLED:
          return "#1565C0";
        case Constants.TRAINING_ENROLLMENT_STATUS_REJECTED:
          return "#C62828";
        case Constants.TRAINING_ENROLLMENT_STATUS_COMPLETED:
          return "#2E7D32";
        case Constants.TRAINING_ENROLLMENT_STATUS_NOT_PASS:
          return "#5D4037";
        case Constants.TRAINING_ENROLLMENT_STATUS_NO_SHOW:
          return "#455A64";
        case Constants.TRAINING_ENROLLMENT_STATUS_CANCELED:
          return "#757575";
        default:
          return "#607D8B";
      }
    },
    enrollmentStatusCount(status) {
      return (this.trainingEnrollments || []).filter((item) => item && item.status === status).length;
    },
    formatTrainingSimpleDate(value) {
      const date = this.convertSimpleDateToDate(value);
      return date ? date.toLocaleDateString() : "";
    },
    deleteItem() {
      this.loading = true;

      api.getTrainingActionEnrollmentCount(
        this.data.id,
        (count) => {
          if (count > 0) {
            this.loading = false;
            this.$emit("show-alert", {
              text: this.$t("wkt.admin.training-action.edit-window.associated_items_error").replace("{0}", count),
              color: "error",
              timeout: 5000,
            });
            return;
          }

          api.deleteTrainingAction(
            this.data.id,
            (res) => {
              this.loading = false;
              if (res.rsOK) {
                this.showDialog = false;
                this.$emit("refresh");
              } else {
                this.$emit("show-alert", {
                  text: res.message,
                  color: "error",
                  timeout: 3000,
                });
              }
            },
            (error) => {
              this.loading = false;
              this.$emit("show-alert", {
                text: error && error.message ? error.message : error,
                color: "error",
                timeout: 3000,
              });
            },
          );
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    getVisibilityItemText(item) {
      if (!item) {
        return "";
      }
      if (this.data.visibilityType === 1) {
        return item.legalEntityName || item.name || "";
      }
      if (this.data.visibilityType === 4) {
        return item.shortName
          ? `${item.shortName}${item.description ? ` - ${item.description}` : ""}`
          : item.description || "";
      }
      if (this.data.visibilityType === 5) {
        return `${item.lastname || ""}, ${item.firstname || ""}`;
      }
      return item.name || "";
    },
    handleAsyncError(error) {
      this.loading = false;
      this.$emit("show-alert", {
        text: error && error.message ? error.message : error,
        color: "error",
        timeout: 3000,
      });
    },
    updateDateFormatted() {
      this.enrollmentStartDateFormatted = this.enrollmentStartDate ? this.formatISOStringDateToLocalDate(this.enrollmentStartDate) : null;
      this.enrollmentEndDateFormatted = this.enrollmentEndDate ? this.formatISOStringDateToLocalDate(this.enrollmentEndDate) : null;
      this.realizationStartDateFormatted = this.realizationStartDate ? this.formatISOStringDateToLocalDate(this.realizationStartDate) : null;
      this.realizationEndDateFormatted = this.realizationEndDate ? this.formatISOStringDateToLocalDate(this.realizationEndDate) : null;
    },
  },
  watch: {
    enrollmentStartDate() { this.enrollmentDateError = false; this.updateDateFormatted(); },
    enrollmentEndDate() { this.enrollmentDateError = false; this.updateDateFormatted(); },
    realizationStartDate() { this.updateDateFormatted(); },
    realizationEndDate() { this.updateDateFormatted(); },
  },
};
</script>
